/**
 * CountdownChip — single source of truth for live countdown rendering.
 * iter113 adds "final-minute drama":
 *
 *   sec > 120  → muted text, no animation                    (default)
 *   sec ≤ 120  → bold + amber tone, slow 1s heartbeat pulse
 *   sec ≤  30  → bold + rose tone, faster pulse
 *   sec ≤  10  → bold + rose + per-tick scale pulse (clock-tick feel)
 *
 * Renders inline (no block layout) so it can drop into any existing badge or paragraph.
 *
 * Optional `tone="strong"` forces the chip to render as a small filled pill instead
 * of just inline text — used by the Sessions Rail headline.
 */
import { formatCountdown } from '../hooks/useSessionLifecycle';

function dramaClasses(sec, isPill) {
  if (sec == null) return '';
  if (sec <= 10) {
    return isPill
      ? 'bg-rose-500 text-white font-bold animate-[countdown-tick_1s_ease-in-out_infinite] shadow-[0_0_12px_rgba(244,63,94,0.6)]'
      : 'text-rose-600 dark:text-rose-300 font-bold animate-[countdown-tick_1s_ease-in-out_infinite]';
  }
  if (sec <= 30) {
    return isPill
      ? 'bg-rose-500/85 text-white font-bold animate-[countdown-heartbeat_1.2s_ease-in-out_infinite]'
      : 'text-rose-600 dark:text-rose-300 font-bold animate-[countdown-heartbeat_1.2s_ease-in-out_infinite]';
  }
  if (sec <= 120) {
    return isPill
      ? 'bg-amber-500/90 text-white font-bold animate-[countdown-heartbeat_2s_ease-in-out_infinite]'
      : 'text-amber-600 dark:text-amber-300 font-bold animate-[countdown-heartbeat_2s_ease-in-out_infinite]';
  }
  return '';
}

export default function CountdownChip({ seconds, prefix, suffix, isPill = false, className = '', testId }) {
  if (seconds == null) return null;
  const drama = dramaClasses(seconds, isPill);
  const text = formatCountdown(seconds);
  const inner = (
    <>
      {prefix ? `${prefix} ` : ''}<span className={drama} data-testid={testId}>{text}</span>{suffix ? ` ${suffix}` : ''}
    </>
  );
  return <span className={className}>{inner}</span>;
}
